import React from 'react';
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { When } from 'react-if';
import dayjs from 'dayjs';
import { GoogleMap, Marker } from '@react-google-maps/api';

import { AppointmentTypes } from '../../types/appointment.types';
import { AppointmentStatusCell } from '../lists/appointments-list/appointment-status-cell';
import { PingIndicator } from '../lists/appointments-list/ping-indicator';
import { deriveLastPingTime } from '../lists/users-list/last-ping-source';

interface Props {
    onClose(): void;
    visible: boolean;
    selectedAppointment: AppointmentTypes.Model;
}

type DrawerContext = 'live' | 'future' | 'past';

/** Consistent field label */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
        sx={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.disabled',
            mb: 0.25,
        }}
    >
        {children}
    </Typography>
);

/** Consistent field value */
const FieldValue: React.FC<{
    children: React.ReactNode;
    color?: string;
    fontWeight?: number;
}> = ({ children, color, fontWeight }) => (
    <Typography
        sx={{
            fontSize: 14,
            fontWeight: fontWeight ?? 400,
            color: color ?? 'text.primary',
            wordBreak: 'break-word',
        }}
    >
        {children}
    </Typography>
);

/**
 * Context-aware appointment detail drawer (bottom).
 *
 * - Live: shows admin action buttons (Resolve Check-On-Me, End Shift)
 * - Future: shows duration and last device ping
 * - Past: shows check-in/out times and locations
 */
export const AppointmentDrawer: React.FC<Props> = ({
    onClose,
    visible,
    selectedAppointment,
}) => {
    const theme = useTheme();

    if (!selectedAppointment) return null;

    // Determine context
    const getContext = (): DrawerContext => {
        if (selectedAppointment.isInProgress) return 'live';
        const now = new Date();
        const endDt = selectedAppointment.endDateTime;
        if (!selectedAppointment.isInProgress && ((endDt && new Date(endDt) > now) || new Date(selectedAppointment.startDateTime) > now)) return 'future';
        return 'past';
    };

    const context = getContext();

    const isAlertState =
        selectedAppointment.liveStatus === 'expired' ||
        selectedAppointment.liveStatus === 'escalated';

    const formatElapsed = (minutes: number | null | undefined): string => {
        if (minutes == null) return '--';
        const hrs = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        if (hrs > 0) return `${hrs}h ${mins}m`;
        return `${mins}m`;
    };

    const formatDuration = (): string => {
        const start = selectedAppointment.startDateTime;
        const end = selectedAppointment.endDateTime;
        if (!start || !end) return '--';
        const diffMins = dayjs(end).diff(dayjs(start), 'minute');
        if (diffMins < 60) return `${diffMins} minutes`;
        const hrs = Math.floor(diffMins / 60);
        const mins = diffMins % 60;
        return mins > 0 ? `${hrs}h ${mins}m` : `${hrs} hours`;
    };

    const formatLocation = (
        loc: AppointmentTypes.GpsLocation | undefined,
    ): string => {
        if (!loc) return '--';
        if (loc.address) return loc.address;
        if (loc.latitude && loc.longitude)
            return `${loc.latitude}, ${loc.longitude}`;
        return '--';
    };

    // Determine map coordinates — prefer check-in location, fall back to appointment coords
    const mapLat = selectedAppointment.checkInLocation?.latitude
        ? Number(selectedAppointment.checkInLocation.latitude)
        : selectedAppointment.coordinates?.latitude
            ? Number(selectedAppointment.coordinates.latitude)
            : null;
    const mapLng = selectedAppointment.checkInLocation?.longitude
        ? Number(selectedAppointment.checkInLocation.longitude)
        : selectedAppointment.coordinates?.longitude
            ? Number(selectedAppointment.coordinates.longitude)
            : null;
    const hasMapCoords = mapLat != null && mapLng != null && !isNaN(mapLat) && !isNaN(mapLng);

    return (
        <Drawer anchor="bottom" open={visible} onClose={onClose}>
            <Box sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
                {/* ── Header ────────────────────────────────────────── */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ px: 3, pt: 2.5, pb: 1.5 }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'text.primary' }}>
                            {selectedAppointment.title || 'Appointment Details'}
                        </Typography>
                        {context === 'live' && selectedAppointment.liveStatus && (
                            <AppointmentStatusCell
                                status={selectedAppointment.liveStatus}
                                variant="live"
                            />
                        )}
                        {context === 'past' && selectedAppointment.pastStatus && (
                            <AppointmentStatusCell
                                status={selectedAppointment.pastStatus}
                                variant="past"
                            />
                        )}
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>

                <Divider />

                {/* ── Detail fields ─────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: 2.5,
                        px: 3,
                        py: 2,
                    }}
                >
                    {/* Common fields */}
                    <Box>
                        <FieldLabel>Scheduled Time</FieldLabel>
                        <FieldValue>
                            {selectedAppointment.startDateTime
                                ? dayjs(selectedAppointment.startDateTime).format('MM/DD/YYYY h:mm A')
                                : '--'}
                        </FieldValue>
                    </Box>

                    <Box>
                        <FieldLabel>User</FieldLabel>
                        <FieldValue>
                            {selectedAppointment.user?.firstName && selectedAppointment.user?.lastName
                                ? `${selectedAppointment.user.firstName} ${selectedAppointment.user.lastName}`
                                : '--'}
                        </FieldValue>
                    </Box>

                    <Box>
                        <FieldLabel>Appointment Location</FieldLabel>
                        <FieldValue color={selectedAppointment.location || selectedAppointment.coordinates ? undefined : 'text.disabled'}>
                            {selectedAppointment.location
                                || (selectedAppointment.coordinates
                                    ? `${selectedAppointment.coordinates.latitude}, ${selectedAppointment.coordinates.longitude}`
                                    : 'No location set')}
                        </FieldValue>
                    </Box>

                    {/* ── Live appointment fields ── */}
                    {context === 'live' && (
                        <>
                            <Box>
                                <FieldLabel>Check-In</FieldLabel>
                                <FieldValue>
                                    {selectedAppointment.checkIn
                                        ? dayjs(selectedAppointment.checkIn).format('h:mm A')
                                        : '--'}
                                </FieldValue>
                            </Box>

                            <Box>
                                <FieldLabel>Checked-In Location</FieldLabel>
                                <FieldValue>
                                    {formatLocation(selectedAppointment.checkInLocation ?? undefined)}
                                </FieldValue>
                            </Box>

                            <Box>
                                <FieldLabel>Elapsed</FieldLabel>
                                <FieldValue
                                    color={isAlertState ? theme.palette.error.main : undefined}
                                    fontWeight={isAlertState ? 600 : 400}
                                >
                                    {formatElapsed(selectedAppointment.elapsed)}
                                </FieldValue>
                            </Box>
                        </>
                    )}

                    {/* ── Future appointment fields ── */}
                    {context === 'future' && (
                        <>
                            <Box>
                                <FieldLabel>Duration</FieldLabel>
                                <FieldValue>{formatDuration()}</FieldValue>
                            </Box>

                            <Box>
                                <FieldLabel>Last Ping</FieldLabel>
                                <PingIndicator
                                    lastPing={deriveLastPingTime(selectedAppointment.user, [
                                        selectedAppointment.lastDevicePing,
                                    ])}
                                />
                            </Box>
                        </>
                    )}

                    {/* ── Past appointment fields ── */}
                    {context === 'past' && (
                        <>
                            <Box>
                                <FieldLabel>Check-In</FieldLabel>
                                <FieldValue>
                                    {selectedAppointment.checkIn
                                        ? dayjs(selectedAppointment.checkIn).format('h:mm A')
                                        : '--'}
                                </FieldValue>
                            </Box>

                            <Box>
                                <FieldLabel>Checked-In Location</FieldLabel>
                                <FieldValue>
                                    {formatLocation(selectedAppointment.checkInLocation ?? undefined)}
                                </FieldValue>
                            </Box>

                            <Box>
                                <FieldLabel>Check-Out</FieldLabel>
                                <FieldValue>
                                    {selectedAppointment.checkOut
                                        ? dayjs(selectedAppointment.checkOut).format('h:mm A')
                                        : '--'}
                                </FieldValue>
                            </Box>

                            <Box>
                                <FieldLabel>Check-Out Location</FieldLabel>
                                <FieldValue>
                                    {formatLocation(selectedAppointment.checkOutLocation ?? undefined)}
                                </FieldValue>
                            </Box>
                        </>
                    )}
                </Box>

                {/* ── Notes ─────────────────────────────────────────── */}
                <When condition={!!selectedAppointment.notes}>
                    <Box sx={{ px: 3, pb: 2 }}>
                        <FieldLabel>Notes</FieldLabel>
                        <FieldValue>{selectedAppointment.notes}</FieldValue>
                    </Box>
                </When>

                {/* ── Map ───────────────────────────────────────────── */}
                <When condition={hasMapCoords}>
                    <Box sx={{ px: 3, pb: 3 }}>
                        <GoogleMap
                            mapContainerStyle={{ height: 280, borderRadius: 8 }}
                            zoom={15}
                            center={{ lat: mapLat!, lng: mapLng! }}
                        >
                            <Marker position={{ lat: mapLat!, lng: mapLng! }} />
                        </GoogleMap>
                    </Box>
                </When>

                <When condition={!hasMapCoords}>
                    <Box sx={{ px: 3, pb: 3 }}>
                        <Box
                            sx={{
                                height: 160,
                                border: `2px dashed ${theme.palette.grey[300]}`,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: theme.palette.text.disabled,
                            }}
                        >
                            <PlaceOutlinedIcon sx={{ fontSize: 36, mb: 0.5, opacity: 0.4 }} />
                            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                {selectedAppointment.location || 'No location data available'}
                            </Typography>
                        </Box>
                    </Box>
                </When>
            </Box>
        </Drawer>
    );
};
